import { Component, ElementRef, ViewChild, Input, SimpleChanges, HostListener, Renderer2, ViewContainerRef } from '@angular/core';
import { ECirclePercentType } from 'src/app/enums/circle-percent-type.enum';
import { environment } from 'src/environments/environment';
import { CirclePercentMarkerComponent } from '../circle-percent-marker/circle-percent-marker.component';
import { Point } from 'common';

const elementResizeDetectorMaker = require("element-resize-detector");

/**
 * Representa un contador de porcenta circular
 */
@Component({
  selector: 'app-circle-percent-component',
  templateUrl: './circle-percent.component.html',
  styleUrls: ['./circle-percent.component.scss'],
})
export class CirclePercentComponent
{
  ECirclePercentType = ECirclePercentType;

  @Input() auto: boolean;
  @Input() type: ECirclePercentType;
  @Input() percent: number;
  @Input() value: number;
  @Input() objetiveTime: number;
  @Input() timeLimit: number;
  @Input() color:string;
  @Input() line:string;

  @ViewChild('canvas') canvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('text') text: ElementRef<HTMLDivElement>;
  @ViewChild('template', { read: ViewContainerRef }) vcr: ViewContainerRef;

  fontSize:number = 0;
  fontSizeObjetive:number = 0;
  showObjetive = true;

  center = 0;
  init = (270).degreeToRadians();
  radius = 0;
  context: CanvasRenderingContext2D;
  lineWidth = 0;

  private readonly canvasColorVariables = {
    gray: '--winco-dark-gray',
    white: '--winco-brand-white',
    green: '--app-data-score-excellent',
    yellow: '--app-data-score-regular',
    red: '--app-data-score-bad',
    blue: '--winco-blue',
    cyan: '--winco-light-blue',
    lime: '--winco-lime',
  };

  constructor(private renderer: Renderer2,
              private ref:ElementRef) {}

  /**
   * Se ejecuta la primera vez que se carga el componente
   */
  ngOnInit()
  {
    elementResizeDetectorMaker().listenTo(this.ref.nativeElement, (element) =>
    {
      this.drawGraphic();
    });
  }

  /**
   * Se ejecuta cuando se descarga el componente
   */
  ngOnDestroy()
  {
    elementResizeDetectorMaker().removeAllListeners(this.ref.nativeElement);
  }

  /**
   * Se ejecuta después de que se haya actualizado la vista
   */
  ngAfterViewInit(): void
  {
    if(this.auto)
      this.drawGraphic();
  }

  /**
   * Se ejecuta cuando se producen cambios en la vista
   */
  ngOnChanges(changes: SimpleChanges): void
  {
    if(this.canvas && this.auto)
      this.drawGraphic();
  }

  /**
   * Dibuja el contador de porcentaje circular
   */
  drawGraphic()
  {
    const div = 3.5;
    this.fontSize = Math.round(this.ref.nativeElement.offsetWidth / div);
    this.fontSizeObjetive = Math.round(this.ref.nativeElement.offsetWidth * 1.25 / 20); //1.25 es el alto deseado en ems y 20 es el ancho maximo en ems
    this.renderer.setStyle(this.text.nativeElement, 'font-size', this.fontSize + 'px');

    this.lineWidth = parseFloat(window.getComputedStyle(document.getElementsByTagName('html')[0], null).getPropertyValue('font-size'));

    if(this.line == 'thin')
      this.lineWidth = this.lineWidth / 2;

    const width = this.canvas.nativeElement.width;

    this.center = width / 2;
    this.radius = this.center - this.lineWidth - 5;
    this.context = this.canvas.nativeElement.getContext("2d");

    this.canvas.nativeElement.height = width;

    this.context.clearRect(0, 0, width, width);

    const colors = this.getCanvasColors();

    this.drawArcLine(0, (360).degreeToRadians(), colors.gray);

    if(this.type === ECirclePercentType.TIME)
    {
      //pintamos los circulos

      const logicLimit = Math.round(this.objetiveTime * environment.minLimitTimePercent / 100);
      const minTime = this.objetiveTime - logicLimit;

      const optimalDegree = (this.objetiveTime * 360 / this.timeLimit).degreeToRadians() + this.init;
      const valueDegree = ((this.value >= this.timeLimit) ? 360 : (this.value * 360 / this.timeLimit)).degreeToRadians() + this.init;

      const color = (this.value >= minTime && this.value <= this.objetiveTime) ? colors.green : ((this.value > this.objetiveTime && this.value <= this.timeLimit) ? colors.yellow : colors.red);

      this.drawArcLine(this.init, valueDegree, color);

      //pintamos los marcadores
      this.vcr.clear();

      this.addMarker('optimo', this.objetiveTime, optimalDegree);
      this.addMarker('limite', this.timeLimit, this.init);
    }
    else
    {
      const degree = (this.percent === 0) ? 0: (360 * this.percent / 100).degreeToRadians() + this.init;
      const color = (this.color === 'blue') ? colors.blue : ((this.color === 'cyan') ? colors.cyan : colors.lime);

      if(degree)
        this.drawArcLine(this.init, degree, color);
    }
  }

  addMarker(title:string, time:number, degree:number)
  {
    const component = this.vcr.createComponent(CirclePercentMarkerComponent);
    component.instance.time = time;
    component.instance.title = title;

    const element = component.location.nativeElement,
          internal = this.getArcCoords(degree, this.center, this.radius),
          external = this.getArcCoords(degree, this.center, this.center),
          externalPlus = this.getArcCoords(degree, this.center, this.center + 10),
          dif = this.ref.nativeElement.offsetWidth / this.canvas.nativeElement.width,
          realDegrees = (degree - this.init).radiansToDegree();

    this.renderer.setStyle(component.location.nativeElement, 'font-size', this.fontSizeObjetive + 'px');

    const colors = this.getCanvasColors();

    this.drawLine(internal, external, colors.gray, 9);
    this.drawLine(internal, externalPlus, colors.white, 3);

    this.renderer.setStyle(element, 'left', (external.x * dif) + 'px');
    this.renderer.setStyle(element, 'top', (external.y * dif) + 'px');

    if(realDegrees == 360 || (realDegrees >= 0 && realDegrees <= 90))
      this.renderer.setStyle(element, 'transform', 'translate(0, -125%)');
    else if(realDegrees > 90 && realDegrees <= 180)
      this.renderer.setStyle(element, 'transform', 'translate(0, 25%)');
    else if(realDegrees > 180 && realDegrees <= 270)
      this.renderer.setStyle(element, 'transform', 'translate(-100%, 50%)');
    else
      this.renderer.setStyle(element, 'transform', 'translate(-100%, -125%)');

    if((realDegrees > 45 && realDegrees < 135) || (realDegrees > 225 && realDegrees < 315))
      this.renderer.addClass(element, 'wrap');
    else
      this.renderer.removeClass(element, 'wrap');
  }

  drawArcLine(init:number, degree:number, color:string)
  {
    this.context.beginPath();
    this.context.arc(this.center, this.center, this.radius, init, degree);
    this.context.strokeStyle = color;
    this.context.lineWidth = this.lineWidth;
    this.context.stroke();
  }

  drawLine(internal:Point, external:Point, color:string, lineWidth:number)
  {
    this.context.beginPath();
    this.context.moveTo(internal.x, internal.y);
    this.context.lineTo(external.x, external.y);
    this.context.strokeStyle = color;
    this.context.lineWidth = lineWidth;
    this.context.stroke();
  }

  private getCanvasColors()
  {
    const computedStyle = window.getComputedStyle(document.documentElement);

    return {
      gray: this.getCssColor(this.canvasColorVariables.gray, computedStyle),
      white: this.getCssColor(this.canvasColorVariables.white, computedStyle),
      green: this.getCssColor(this.canvasColorVariables.green, computedStyle),
      yellow: this.getCssColor(this.canvasColorVariables.yellow, computedStyle),
      red: this.getCssColor(this.canvasColorVariables.red, computedStyle),
      blue: this.getCssColor(this.canvasColorVariables.blue, computedStyle),
      cyan: this.getCssColor(this.canvasColorVariables.cyan, computedStyle),
      lime: this.getCssColor(this.canvasColorVariables.lime, computedStyle),
    };
  }

  private getCssColor(cssVariable:string, computedStyle:CSSStyleDeclaration, resolvedVariables:string[] = []):string
  {
    if(resolvedVariables.includes(cssVariable))
      return '';

    const value = computedStyle.getPropertyValue(cssVariable).trim();
    const nestedVariable = value.match(/^var\((--[\w-]+)(?:,\s*(.+))?\)$/);

    if(nestedVariable)
      return this.getCssColor(nestedVariable[1], computedStyle, [...resolvedVariables, cssVariable]) || (nestedVariable[2]?.trim() ?? '');

    return value;
  }

  /**
   * Se ejcuta cuando se redimensiona la ventana
   */
  @HostListener('window:resize')
  onResize() {
    this.drawGraphic();
  }

  /**
   * Obtiene las coordenadas del punto de finalización de un arco
   * @param angle Ángulo en radianes
   * @param center Punto medio desde donde se traza la circunferencia del arco
   * @param radius Longitud del radio
   * @param lineWidth Grosor de la línea dibujada por el arco
   * @returns
   */
  private getArcCoords(angle:number, center:number, radius:number)
  {
    return  new Point({ x: center + Math.cos(angle) * (radius - (this.lineWidth / 2)), y: center + Math.sin(angle) * (radius - (this.lineWidth / 2)) });
  }

  /**
   * Muestra u oculta el objetivo
   * @param show boolean mostrar
   */
  toggleShowObjetive(show:boolean)
  {
    this.showObjetive = show;
  }
}

